import { useState, useEffect, useRef, useCallback } from "react";
import { MessageSquare, MoreHorizontal, Loader2 } from "lucide-react";
import { avatarColors } from "./data";
import axiosInstance from "../../API/interceptor";
import TaskDetailPanel from "./TaskDetailPanel";
import WorkspaceDropdown from "./WorkspaceDropdown";
import TeamDropdown from "./TeamDropdown";

/* ══ COLUMN CONFIG ════════════════════════════════════════════ */
const COLUMNS = [
  { key: "open",             label: "Open",             apiKey: "open",             color: "#7C3AED" },
  { key: "in progress",      label: "In Progress",      apiKey: "in progress",      color: "#0D9488" },
  { key: "on hold",          label: "On Hold",          apiKey: "on hold",          color: "#DB2777" },
  { key: "completed",        label: "Complete",         apiKey: "completed",        color: "#059669" },
  { key: "approval pending", label: "Approval Pending", apiKey: "approval pending", color: "#F59E0B" },
];


const LIMIT = 8;

/* ══ PRIORITY CONFIG ══════════════════════════════════════════ */
const PRIORITY_CFG = {
  HIGH:     { color: "#D97706", bg: "#FEF9C3", border: "#FDE68A", dot: "#D97706" },
  MEDIUM:   { color: "#0D9488", bg: "#CCFBF1", border: "#99F6E4", dot: "#0D9488" },
  LOW:      { color: "#16A34A", bg: "#DCFCE7", border: "#BBF7D0", dot: "#16A34A" },
  URGENT:   { color: "#EA580C", bg: "#FFF7ED", border: "#FED7AA", dot: "#EA580C" },
  CRITICAL: { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", dot: "#DC2626" },
};

const getPc = p => PRIORITY_CFG[(p || "MEDIUM").toUpperCase()] || PRIORITY_CFG["MEDIUM"];

/* ══ HELPERS ══════════════════════════════════════════════════ */
const avatarBg = name => avatarColors[(name || "?").charCodeAt(0) % avatarColors.length];

function fmtDateTime(v) {
  if (!v) return "--";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "--";
    const dd   = String(d.getDate()).padStart(2, "0");
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const h24  = d.getHours();
    const h12  = h24 % 12 || 12;
    const ampm = h24 >= 12 ? "PM" : "AM";
    const mi   = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()} ${String(h12).padStart(2,"0")}:${mi} ${ampm}`;
  } catch { return "--"; }
}

/* ══ MINI AVATAR ══════════════════════════════════════════════ */
function MiniAvatar({ name, size = 28 }) {
  return (
    <div className="rounded-full flex items-center justify-center text-white font-semibold border-2 border-white shrink-0 shadow-[0_1px_3px_rgba(0,0,0,.15)] overflow-hidden -ml-1.5"
      style={{ width:size, height:size, background:avatarBg(name||"?"), fontSize:Math.round(size*0.38) }}>
      {(name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

/* ══ TASK CARD ════════════════════════════════════════════════ */
function TaskCard({ task, col, isDragging, onDragStart, onDragEnd, onTitleClick }) {
  const pc           = getPc(task.priority);
  const isCompleted  = col.key === "completed";
  const deadlineColor =
    task.sla_status === "danger"  ? "#DC2626" :
    task.sla_status === "warning" ? "#D97706" :
    task.sla_status === "success" ? "#16A34A" : "#9CA3AF";

  const assigneeNames    = Array.isArray(task.assigned_to_names) ? task.assigned_to_names : [];
  const notesCount       = task.notes_count || (Array.isArray(task.notes) ? task.notes.length : 0);
  const attachmentsCount = Array.isArray(task.s3_name) ? task.s3_name.length : 0;

  return (
    <div
      draggable={!isCompleted}
      onDragStart={!isCompleted ? e => onDragStart(e, task._id) : undefined}
      onDragEnd={onDragEnd}
      className={`bg-white border border-[#e0e0e0] rounded-lg mb-[10px] overflow-hidden transition-all duration-200 relative hover:shadow-[0_4px_8px_rgba(0,0,0,.1)] hover:-translate-y-0.5 ${
        isCompleted ? "cursor-default" : "cursor-grab"
      }`}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      {/* Card body */}
      <div className="p-[10px_12px]">

        {/* Row 1 — ID + Priority badge + ... */}
        <div className="flex items-center justify-between mb-[6px]">
          <span className="font-mono text-[11px] font-semibold text-[#6366F1]">#{task.unique_seq_id}</span>
          <div className="flex items-center gap-[6px]">
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-[3px] rounded border"
              style={{ background:pc.bg, color:pc.color, borderColor:pc.border }}
            >
              <span className="w-[6px] h-[6px] rounded-full inline-block shrink-0" style={{ background:pc.dot }}/>
              {(task.priority || "MEDIUM").toUpperCase()}
            </span>
            <button className="bg-transparent border-none cursor-pointer text-[#CBD5E1] p-0 flex items-center">
              <MoreHorizontal size={14} />
            </button>
          </div>
        </div>

        {/* Task title */}
        <div
          onClick={(e) => { e.stopPropagation(); onTitleClick(task._id); }}
          className="text-[13px] font-semibold text-[#1F2937] mb-[10px] leading-[1.4] overflow-hidden cursor-pointer [-webkit-line-clamp:2] [-webkit-box-orient:vertical] [display:-webkit-box] [word-break:break-word] hover:text-[#2563EB] hover:underline"
        >
          {task.title}
        </div>

        {/* Priority row */}
        <div className="flex items-center mb-[5px]">
          <span className="text-[12px] text-[#9CA3AF] min-w-[70px]">Priority:</span>
          <span className="text-[12px] font-medium" style={{ color:pc.color }}>{(task.priority||"MEDIUM").toUpperCase()}</span>
        </div>

        {/* Due date row */}
        <div className="flex items-center mb-[5px]">
          <span className="text-[12px] text-[#9CA3AF] min-w-[70px]">Due Date:</span>
          <span
            className={`text-[12px] ${task.sla_status === "danger" ? "font-bold" : "font-normal"}`}
            style={{ color:deadlineColor }}
          >
            {fmtDateTime(task.completion_sla)}
          </span>
        </div>

        {/* Workspace row */}
        <div className="flex items-center mb-[5px]">
          <span className="text-[12px] text-[#9CA3AF] min-w-[70px]">Workspace:</span>
          {task.product_name
            ? <span className="text-[11px] font-semibold px-2 py-[1px] rounded-full bg-[#EFF6FF] text-[#2563EB] border border-[#BFDBFE]">{task.product_name}</span>
            : <span className="text-[12px] text-[#D1D5DB]">—</span>
          }
        </div>

        {/* Responsible row */}
        <div className="flex items-center mb-2">
          <span className="text-[12px] text-[#9CA3AF] min-w-[70px]">Responsible:</span>
          <div className="flex items-center pl-[6px]">
            {assigneeNames.length === 0
              ? <span className="text-[12px] text-[#D1D5DB]">—</span>
              : assigneeNames.slice(0, 4).map((name, ni) => <MiniAvatar key={ni} name={name} size={28} />)
            }
          </div>
        </div>

        {/* Bottom — comments + attachments */}
        <div className="flex items-center justify-between border-t border-[#F3F4F6] pt-2">
          <span className="flex items-center gap-1 text-[12px] text-[#9CA3AF]">
            <MessageSquare size={12} /> {notesCount} Comments
          </span>
          <span className="flex items-center gap-1 text-[12px] text-[#9CA3AF]">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
            {attachmentsCount} Attachments
          </span>
        </div>
      </div>

      {/* Bottom colored border */}
      <div className="h-[3px]" style={{ background:pc.color }} />
    </div>
  );
}

/* ══ COLUMN ═══════════════════════════════════════════════════ */
function Column({ col, filters, refreshKey, dragId, onDragStart, onDragEnd, onDrop, onTitleClick }) {
  const [tasks,    setTasks]    = useState([]);
  const [count,    setCount]    = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [isOver,   setIsOver]   = useState(false);

  const scrollRef   = useRef();
  const loadingRef  = useRef(false);
  const endRef      = useRef(false);
  const offsetRef   = useRef(0);

  const fetchTasks = useCallback(async (currentOffset, reset = false) => {
    if (loadingRef.current || (endRef.current && !reset)) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/task_assignment/get_tracker_data/${encodeURIComponent(col.apiKey)}`,
        {
          params: {
            product_code: filters.product_code || 0,
            task_type:    filters.task_type    || "",
            limit:        LIMIT,
            offset:       currentOffset,
          },
        }
      );
      const newTasks = res.data?.tasks || [];
      const total    = res.data?.count  ?? 0;
      setCount(total);
      if (reset) {
        setTasks(newTasks);
      } else {
        setTasks(prev => {
          const ids = new Set(prev.map(t => t._id));
          return [...prev, ...newTasks.filter(t => !ids.has(t._id))];
        });
      }
      const newOffset = currentOffset + newTasks.length;
      offsetRef.current = newOffset;
      if (newTasks.length < LIMIT) endRef.current = true;
    } catch (err) {
      console.error(`WorkTracker [${col.key}]:`, err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [col.apiKey, filters]);

  useEffect(() => {
    setTasks([]);
    setCount(0);
    offsetRef.current = 0;
    endRef.current    = false;
    fetchTasks(0, true);
  }, [filters, refreshKey]); // eslint-disable-line

  // Infinite scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60
        && !loadingRef.current && !endRef.current) {
        fetchTasks(offsetRef.current);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [fetchTasks]);

  const isApproval      = col.key === "approval pending";
  const handleDragOver  = e => { e.preventDefault(); setIsOver(true); };
  const handleDragLeave = () => setIsOver(false);
  const handleDrop      = e => { e.preventDefault(); setIsOver(false); if (!isApproval) onDrop(e, col.key); };

  return (
    <div className="flex-1 min-w-0 flex flex-col"
      onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>

      {/* Column header */}
      <div className="flex justify-between items-center px-[14px] py-[10px] mb-1 rounded-[6px] text-white"
        style={{ background:col.color }}>
        <span className="font-bold text-[14px]">{col.label}</span>
        <span className="font-black text-[12px] w-7 h-7 rounded-full flex items-center justify-center bg-white/25">{count}</span>
      </div>

      {/* Column body */}
      <div ref={scrollRef}
        className="flex-1 p-[8px_8px_24px] rounded overflow-y-auto min-h-[500px] max-h-[calc(100vh-220px)] transition-[background,box-shadow] duration-150 border"
        style={{
          background: isOver ? "#F5F3FF" : "#F9FAFB",
          borderColor: isOver ? col.color : "#E5E7EB",
          boxShadow: isOver ? `inset 0 0 0 2px ${col.color}` : "none",
        }}
      >
        {/* Drop zone */}
        {isOver && dragId && !isApproval && (
          <div className="rounded-lg py-[14px] text-center text-[12px] font-semibold bg-[#F5F3FF] mb-2 border-2 border-dashed"
            style={{ color:col.color, borderColor:col.color }}>Drag Here</div>
        )}

        {tasks.length === 0 && !loading ? (
          <div className="text-center py-10 text-[#D1D5DB] text-[12px]">No tasks</div>
        ) : tasks.map(task => (
          <TaskCard key={task._id} task={task} col={col} isDragging={dragId===task._id}
            onDragStart={onDragStart} onDragEnd={onDragEnd} onTitleClick={onTitleClick}/>
        ))}

        {loading && (
          <div className="flex justify-center py-[10px]">
            <Loader2 size={18} className="animate-spin" style={{ color:col.color }}/>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══ MAIN WORK TRACKER ════════════════════════════════════════ */
export default function WorkTracker({ refreshKey = 0 }) {
  const [filters, setFilters] = useState({
    product_code: 0,
    task_type:    "self",
  });
  const [workspaceOpts, setWorkspaceOpts] = useState([]);
  const [dragId,        setDragId]        = useState(null);
  const [toast,         setToast]         = useState(null);
  const [detailTaskId,  setDetailTaskId]  = useState(null);

  useEffect(() => {
    axiosInstance.get("/task_assignment/get_product_codes")
      .then(res => {
        const arr = Array.isArray(res.data?.product_codes) ? res.data.product_codes
                  : Array.isArray(res.data) ? res.data : [];
        setWorkspaceOpts(arr.map(item => {
          if (typeof item === "object") {
            const key = Object.keys(item)[0];
            return { value: key, label: item[key] };
          }
          return null;
        }).filter(Boolean));
      })
      .catch(() => {});
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const handleDragEnd   = () => setDragId(null);

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!dragId || newStatus === "approval pending") return;
    const prevId = dragId;
    setDragId(null);

    if (["closed", "completed"].includes(newStatus)) {
      try {
        const detailRes = await axiosInstance.get(`/task_assignment/get_task_details/${prevId}`);
        const taskDetail = detailRes.data?.task || detailRes.data;
        const subs = Array.isArray(taskDetail?.subtasks) ? taskDetail.subtasks : [];
        const incomplete = subs.filter(s =>
          !["completed", "complete", "closed"].includes((s.task_status || "").toLowerCase())
        );
        if (incomplete.length > 0) {
          showToast(`Cannot ${newStatus} task — ${incomplete.length} subtask(s) are still open.`, "error");
          return;
        }
      } catch { /* let backend decide if fetch fails */ }
    }

    try {
      const res = await axiosInstance.post(
        `/task_assignment/update_status/${prevId}`,
        { new_status: newStatus },
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.data?.success) {
        showToast(`Moved to "${newStatus}"`, "success");
        setFilters(f => ({ ...f }));
      } else {
        showToast(res.data?.message || "Failed", "error");
      }
    } catch {
      showToast("Error updating status", "error");
    }
  };

  return (
    <div className="font-[inherit]">
      <style>{`
        @keyframes wt-spin    { to { transform: rotate(360deg); } }
        @keyframes wt-slideUp { from { opacity:0; transform:translate(-50%,8px); } to { opacity:1; transform:translate(-50%,0); } }
      `}</style>

      {/* Filter bar */}
      <div className="flex items-center gap-[10px] px-5 py-[10px] bg-white border-b border-[#F1F5F9] flex-wrap">
        <div className="w-[200px]">
          <WorkspaceDropdown
            value={filters.product_code && filters.product_code !== 0 ? [String(filters.product_code)] : []}
            onChange={arr => setFilters(f => ({ ...f, product_code: arr.length ? arr[0] : 0 }))}
            options={workspaceOpts}
            single
          />
        </div>
        <div className="w-[160px]">
          <TeamDropdown
            value={filters.task_type}
            onChange={val => setFilters(f => ({ ...f, task_type: val }))}
            allowedValues={["self", "my_key_tasks"]}
          />
        </div>
      </div>

      {/* Kanban board */}
      <div className="overflow-x-auto">
        <div className="flex gap-[10px] p-[16px_15px] min-w-[1100px] w-full box-border items-start">
          {COLUMNS.map(col => (
            <Column key={`${col.key}-${JSON.stringify(filters)}`} col={col} filters={filters} refreshKey={col.key === "open" ? refreshKey : 0}
              dragId={dragId} onDragStart={handleDragStart} onDragEnd={handleDragEnd}
              onDrop={handleDrop} onTitleClick={id => setDetailTaskId(id)}/>
          ))}
        </div>
      </div>

      <TaskDetailPanel taskId={detailTaskId} onClose={() => setDetailTaskId(null)}/>

      {toast && (
        <div className={`fixed bottom-5 left-1/2 -translate-x-1/2 text-white text-[12px] font-semibold px-5 py-2 rounded-full whitespace-nowrap z-[9999] shadow-[0_4px_16px_rgba(0,0,0,.15)] [animation:wt-slideUp_.25s_ease] ${toast.type==="success"?"bg-[#059669]":"bg-[#DC2626]"}`}>
          {toast.type === "success" ? "✅" : "❌"} {toast.msg}
        </div>
      )}
    </div>
  );
}